import clsx from "clsx";

import {
  ITicketSummary,
  ITicketUserSummary,
} from "../../models/Tickets/Ticket";
import { IStaffUser } from "../../models/StaffUser/StaffUser";

interface TicketUserSummaryTableProps {
  summary: ITicketSummary;
  staffUsers?: IStaffUser[];
  selectedAssignee?: string;
  unassignedSelected: boolean;
  onSelectAssignee: (assignedToUserId?: string) => void;
  onSelectUnassigned: () => void;
}

export default function TicketUserSummaryTable({
  summary,
  staffUsers,
  selectedAssignee,
  unassignedSelected,
  onSelectAssignee,
  onSelectUnassigned,
}: TicketUserSummaryTableProps) {
  const rows = summary.byUser ?? [];
  if (rows.length === 0) return null;

  const displayName = (row: ITicketUserSummary) => {
    if (!row.assignedToUserId) return "Unassigned";
    const staff = staffUsers?.find(
      (x) => x.email?.toLowerCase() === row.assignedToUserId?.toLowerCase(),
    );
    return staff?.displayName || row.assignedToUserId;
  };

  const isSelected = (row: ITicketUserSummary) =>
    row.assignedToUserId
      ? selectedAssignee?.toLowerCase() === row.assignedToUserId.toLowerCase()
      : unassignedSelected;

  const handleClick = (row: ITicketUserSummary) => {
    if (!row.assignedToUserId) {
      onSelectUnassigned();
      return;
    }

    onSelectAssignee(isSelected(row) ? undefined : row.assignedToUserId);
  };

  return (
    <div>
      <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
        By User
      </p>
      <div className="inline-block overflow-hidden rounded-md border border-orange-200">
        <table className="min-w-[19rem] divide-y divide-orange-100">
          <thead className="bg-orange-50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">
                Assignee
              </th>
              <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">
                #
              </th>
              <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">
                Done
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-orange-100 bg-white">
            {rows.map((row) => (
              <tr
                key={row.assignedToUserId ?? "unassigned"}
                onClick={() => handleClick(row)}
                className={clsx(
                  "cursor-pointer hover:bg-orange-50",
                  isSelected(row) ? "bg-orange-100" : "",
                )}
              >
                <td
                  className={clsx(
                    "max-w-[12rem] truncate px-3 py-1.5 text-sm",
                    row.assignedToUserId
                      ? "font-medium text-slate-800"
                      : "italic text-gray-500",
                  )}
                  title={row.assignedToUserId ?? "Unassigned"}
                >
                  {displayName(row)}
                </td>
                <td className="px-3 py-1.5 text-right text-sm text-slate-800">
                  {row.active}
                </td>
                <td className="px-3 py-1.5 text-right text-sm text-slate-800">
                  {row.done}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-1 text-xs text-gray-500">
        Click a row to filter the list below.
      </p>
    </div>
  );
}
